import React, { useState, useEffect, useRef } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Refresh from '@mui/icons-material/Refresh';
import Tooltip from "@mui/material/Tooltip";

import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridPrintExportMenuItem,
  GridCsvExportMenuItem,
  GridToolbarExportContainer,
  gridExpandedRowCountSelector,
} from '@mui/x-data-grid';

const axios = require('axios');

const {
  REACT_APP_X_API_KEY,
  REACT_APP_UPDATE_CONF_URL,
  REACT_APP_UPDATE_CONF_KEY
} = process.env;

const marginsHistory = { marginTop: 96, marginBottom: 32, marginLeft: 32, marginRight: 32 };

export default function Stats({ auth }) {
  const navbarRef = useRef();

  const [calcRow, setCalcRow] = useState([]);
  const [editedRowIndex, setEditedRowIndex] = React.useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (auth.userState.userItem) {
      refrehData();
    }
  }, []);

  const refrehData = () => {
    setLoading(true);
    fetchConf()
      .then(response => {
        setCalcRow(response.data.activeProjects.map((row, index) => ({
          ...row,
          id: row.project_id,
          user: row.user,
          risk: row.risk, // row.no_worries ? Math.floor(((Number(row.inactivity_days) - Number(row.no_worries)) * 100) / 365) : Math.floor(((Number(row.inactivity_days)) * 100) / 365),
        })));
        setLoading(false);
      })
  }

  const fetchConf = (projectId) => {
    if (projectId) {
      const customer = projectId.split('/')[0];
      const project_name = projectId.split('/')[1];
      return axios.get('https://api.pagination.com/vpc/projects-webapp',
        {
          headers: {
            'user_id': auth.userState.user,
            "content-type": "application/json",
            "x-api-key": REACT_APP_X_API_KEY || auth.userState.ssmParams.X_API_KEY,
            // "type": 'active-project-list',
            "project_id": projectId,
            "customer": customer,
            "project_name": project_name,
          }

        },
      );
    } else {
      return axios.get('https://api.pagination.com/vpc/projects-webapp',
        {
          headers: {
            'user_id': auth.userState.user,
            "content-type": "application/json",
            "x-api-key": REACT_APP_X_API_KEY || auth.userState.ssmParams.X_API_KEY,
            "type": 'active-project-list',
          }

        },
      );
    }
  }

  const updateNoWorries = (indexRow) => {
    const row = calcRow[indexRow];
    const worriesVal = row.no_worries;

    fetchConf(row.id).then(conf => {
      conf.data.projectConf.no_worries = worriesVal;
      return axios({
        method: "put",
        url: "https://a7z5opolx2.execute-api.eu-west-1.amazonaws.com/vpc/conf", // REACT_APP_UPDATE_CONF_URL || auth.userState.ssmParams.UPDATE_CONF_URL,
        headers: {
          'x-api-key': REACT_APP_UPDATE_CONF_KEY || auth.userState.ssmParams.UPDATE_CONF_KEY,
          'user': 'webapp',
          "content-type": "application/json",
        },
        data: JSON.stringify({ conf: conf.data.projectConf }),
        json: true
      })
    })

  }

  const renderPercentageBar = (req) => {
    const getColor = (value) => {
      if (value > 100) return 'error';
      if (value > 60) return 'warning';
      return 'primary';
    };

    return (
      <Box sx={{ width: '100%' }}>
        <LinearProgress sx={{ height: 10 }} variant="determinate" color={getColor(req.row.risk)} value={req.row.risk > 100 ? 100 : req.row.risk} />
      </Box>
    )
  }

  const calculateRisk = (async (event, value) => {
    const updatedRows = calcRow.map((row, index) => ({
      ...row,
      risk: row.no_worries ? Math.floor(((Number(row.inactivity_days) - Number(row.no_worries)) * 100) / 365) : Math.floor(((Number(row.inactivity_days)) * 100) / 365),
    }));
    setCalcRow(updatedRows);

    // salvare no worries nella conf
    for (const indexRow of editedRowIndex) {
      updateNoWorries(indexRow)
    }
  });

  const handleCellEditCommit = (params) => {
    const rowIndex = calcRow.findIndex((row) => row.id === params.id);
    const curreditedRowIndex = [...editedRowIndex];
    if (!editedRowIndex.includes(rowIndex)) curreditedRowIndex.push(rowIndex);
    setEditedRowIndex(curreditedRowIndex);
  };

  const columns = [
    {
      field: 'customer',
      renderHeader: () => <strong key={'customer'}>{'Customer'}</strong>,
      flex: 3,
    },
    {
      field: 'id',
      renderHeader: () => <strong key={'project_id'}>{'Project id'}</strong>,
      flex: 4,
    },
    {
      field: 'user',
      renderHeader: () => <strong key={'user'}>{'User email'}</strong>,
      flex: 4,
    },
    {
      field: 'inactivity_days',
      renderHeader: () => <strong key={'inactivity_days'}>{'Inactivity Days'}</strong>,
      type: 'number',
      flex: 2,
    },
    {
      field: 'no_worries',
      renderHeader: () => <strong key={'no_worries'}>{'No Worries days'}</strong>,
      type: 'number',
      flex: 2,
      editable: true,
      description: '"Acceptable" days of inactivity for the project (e.g. if the user paginates once a year, indicate for example 300 days)'
    },
    {
      field: 'risk',
      renderHeader: () => <strong key={'risk'}>{'Risk Level %'}</strong>,
      type: 'number',
      flex: 2,
      description: 'Percentage of days at risk over 365 days'
    },
    {
      field: 'risk_bar',
      renderHeader: () => <strong key={'risk_bar'}>{'Risk days / year'}</strong>,
      flex: 2,
      renderCell: renderPercentageBar,
      align: 'left',
      description: 'Percentage of days at risk over 365 days'
    },
  ];

  return (
    <Box>
      <Stack direction='column' sx={{ margin: { ...marginsHistory, marginTop: marginsHistory.marginTop + ((navbarRef.current && navbarRef.current.clientHeight) || 0) }, }} spacing={4}>
        <Card sx={{ padding: '1em' }}>
          <Stack sx={{ m: '1em' }} direction='row' alignItems='center' justifyContent='space-between' spacing={2} >
            <Typography variant='h5' color='primary' >
              <b>At risk client list</b>
            </Typography>
            <Box sx={{ mr: '1em', position: 'relative' }}>
              <Tooltip title="Refresh data from database">
                <IconButton onClick={refrehData} > <Refresh /> </IconButton>
              </Tooltip>
            </Box>
          </Stack>
          <Stack sx={{ ml: '1em', mr: '1em' }} direction='row' alignItems='center' justifyContent='space-between' spacing={2} >
            <Typography variant='caption' color='primary' >
              List of active projects and calculation of the risk of non-use of the service.<br />
              In the "No Worries days" field you can indicate an indicative number of days based on the frequency of requests made (e.g. normally the customer makes a request for project X every 30 days).<br />
              Once the "No Worries days" is indicated, you can click on the "Save and Calculate" button to recalculate the risk.
            </Typography>
          </Stack>
          <Stack sx={{ ml: '1em', mr: '1em' }} direction='row' alignItems='center' justifyContent='space-between' spacing={2} >
            <Box>
              {loading && (
                <Stack direction='row' alignItems='center' >
                  <CircularProgress size="1rem" />
                  <Typography variant='body' color='primary' sx={{ ml: '0.5em' }} >
                    retrieving information ...
                  </Typography>
                </Stack>
              )}
            </Box>
            <Button variant='contained' sx={{ marginRight: '1em' }} onClick={async () => { await calculateRisk() }}>
              Save and Calculate
            </Button>
          </Stack>

          <CardContent >
            <DataGrid
              rows={calcRow}
              columns={columns}
              density='compact'
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
                sorting: {
                  sortModel: [{ field: 'risk', sort: 'desc' }],
                },
              }}
              processRowUpdate={(newRow) => {
                setCalcRow((prevRows) =>
                  prevRows.map((row) => (row.project_id === newRow.project_id ? newRow : row))
                );
                return newRow;
              }}
              pageSizeOptions={[5]}
              // checkboxSelection
              disableRowSelectionOnClick
              onCellEditStop={handleCellEditCommit}
            />
          </CardContent>
        </Card >
      </Stack>
    </Box>
  );
}